// План → Календарь: неделя и месяц, план и факт рядом, перенос, пропуск, разовые, разгрузка.

import { useQuery } from '@tanstack/react-query'
import { useRef, useState, type TouchEvent } from 'react'
import { Link } from 'react-router-dom'

import { api } from '@/api/client'

import { Button, EmptyState, ErrorText, IconButton, PageSpinner, Tabs } from '@/components/ui'
import { cn } from '@/lib/cn'
import { formatDate, formatDayMonth, formatDuration, formatNumber } from '@/lib/format'
import { addDays, todayIso } from '@/lib/periods'
import type { SessionPage, SessionSummary } from '@/pages/workouts/api'

import { useCalendar, useDeloads, type Planned } from './api'
import {
  STATUS_DOT,
  STATUS_META,
  WEEKDAYS_FULL,
  WEEKDAYS_SHORT,
  groupByDay,
  monthBounds,
  monthGrid,
  monthTitle,
  mondayOf,
  sessionDay,
  shiftMonth,
  unplannedSessions,
  weekDays,
  weekdayOf,
} from './logic'
import { AddPlannedModal, DeloadList, MoveModal, PlannedCard } from './parts'

type View = 'week' | 'month'

function useFacts(start: string, end: string) {
  return useQuery({
    queryKey: ['training', 'sessions', { start, end }],
    queryFn: () => api.get<SessionPage>('/sessions', { query: { start, end, limit: 100 } }),
    placeholderData: (previous) => previous,
  })
}

function FactRow({ session }: { session: SessionSummary }) {
  return (
    <Link
      to={`/workouts/${session.id}`}
      className="flex min-h-11 items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm hover:bg-surface-2"
    >
      <span className="min-w-0 flex-1 truncate text-fg">{session.template_name ?? 'Тренировка'} · без плана</span>
      <span className="shrink-0 tabular-nums text-muted">
        {session.duration_s ? formatDuration(session.duration_s) : 'идёт'}
        {session.volume_kg ? ` · ${formatNumber(session.volume_kg, { digits: 0 })} кг` : ''}
      </span>
    </Link>
  )
}

function DayBlock({
  day,
  items,
  facts,
  onMove,
  onAdd,
}: {
  day: string
  items: Planned[]
  facts: SessionSummary[]
  onMove: (p: Planned) => void
  onAdd: (day: string) => void
}) {
  const today = todayIso()
  const isToday = day === today
  return (
    <section aria-label={`${WEEKDAYS_FULL[weekdayOf(day)]}, ${formatDate(day)}`} className="min-w-0">
      <header className="mb-1.5 flex items-center gap-2">
        <h3 className={cn('text-sm font-semibold', isToday ? 'text-accent' : 'text-fg')}>
          {WEEKDAYS_SHORT[weekdayOf(day)]}, {formatDayMonth(day)}
          {isToday ? ' · сегодня' : ''}
        </h3>
        <span className="flex-1" />
        {day >= today ? (
          <IconButton icon="plus" label={`Добавить тренировку на ${formatDate(day)}`} onClick={() => onAdd(day)} />
        ) : null}
      </header>
      {items.length || facts.length ? (
        <div className="space-y-2">
          {items.map((p) => (
            <PlannedCard key={p.id} item={p} onMove={onMove} />
          ))}
          {facts.map((s) => (
            <FactRow key={s.id} session={s} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted">Отдых</p>
      )}
    </section>
  )
}

function Legend() {
  return (
    <ul className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted" aria-label="Обозначения">
      {Object.entries(STATUS_META).map(([status, meta]) => (
        <li key={status} className="flex items-center gap-1.5">
          <span className={cn('h-2 w-2 rounded-full', STATUS_DOT[status as Planned['status']])} />
          {meta.label}
        </li>
      ))}
    </ul>
  )
}

export function CalendarPage() {
  const [view, setView] = useState<View>('week')
  const [anchor, setAnchor] = useState(todayIso())
  const [selected, setSelected] = useState<string | null>(null)
  const [moving, setMoving] = useState<Planned | null>(null)
  const [addDay, setAddDay] = useState<string | null>(null)
  const touch = useRef<number | null>(null)

  const range =
    view === 'week' ? { start: mondayOf(anchor), end: addDays(mondayOf(anchor), 6) } : monthBounds(anchor)
  const calendar = useCalendar(range.start, range.end)
  const facts = useFacts(range.start, range.end)
  const deloads = useDeloads()

  const planned = calendar.data ?? []
  const groups = groupByDay(planned)
  const factsByDay: Record<string, SessionSummary[]> = {}
  for (const s of unplannedSessions(facts.data?.items ?? [], planned)) {
    ;(factsByDay[sessionDay(s.started_at)] ??= []).push(s)
  }

  const shift = (delta: number) => {
    setSelected(null)
    setAnchor(view === 'week' ? addDays(anchor, delta * 7) : shiftMonth(anchor, delta))
  }
  // Свайп по неделе/месяцу — листание без горизонтальной прокрутки страницы.
  const onTouchStart = (e: TouchEvent) => {
    touch.current = e.touches[0]?.clientX ?? null
  }
  const onTouchEnd = (e: TouchEvent) => {
    const start = touch.current
    const end = e.changedTouches[0]?.clientX
    touch.current = null
    if (start === null || end === undefined || Math.abs(end - start) < 60) return
    shift(end < start ? 1 : -1)
  }

  const title =
    view === 'week' ? `${formatDayMonth(range.start)} – ${formatDate(range.end)}` : monthTitle(anchor)
  const dayBlock = (day: string) => (
    <DayBlock
      key={day}
      day={day}
      items={groups[day] ?? []}
      facts={factsByDay[day] ?? []}
      onMove={setMoving}
      onAdd={setAddDay}
    />
  )

  return (
    <div className="min-w-0">
      <DeloadList items={deloads.data ?? []} />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Tabs
          label="Вид календаря"
          items={[
            { value: 'week', label: 'Неделя' },
            { value: 'month', label: 'Месяц' },
          ]}
          value={view}
          onChange={(v) => {
            setView(v)
            setSelected(null)
          }}
        />
        <span className="flex-1" />
        <Button size="sm" icon="plus" onClick={() => setAddDay(todayIso())}>
          Разовая
        </Button>
      </div>
      <div className="mb-3 flex items-center gap-1">
        <IconButton icon="chevron-left" label="Назад" onClick={() => shift(-1)} />
        <h2 className="min-w-0 flex-1 truncate text-center text-base font-semibold tabular-nums text-fg">{title}</h2>
        <IconButton icon="chevron-right" label="Вперёд" onClick={() => shift(1)} />
      </div>
      <div className="mb-3 flex justify-center">
        <Button size="sm" onClick={() => setAnchor(todayIso())}>
          Сегодня
        </Button>
      </div>
      <Legend />

      <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} className="min-w-0">
        {calendar.isPending ? (
          <PageSpinner />
        ) : calendar.error ? (
          <ErrorText error={calendar.error} />
        ) : view === 'week' ? (
          <>
            {planned.length === 0 && !Object.keys(factsByDay).length ? (
              <EmptyState
                icon="plan"
                title="На эту неделю ничего не запланировано"
                description="Задай расписание по дням недели — план на 4 недели появится сам."
                action={
                  <Link to="/plan/rules" className="text-sm font-medium text-accent hover:underline">
                    Открыть расписание
                  </Link>
                }
              />
            ) : null}
            <div className="space-y-4">{weekDays(anchor).map(dayBlock)}</div>
          </>
        ) : (
          <>
            <div role="grid" aria-label={monthTitle(anchor)} className="grid grid-cols-7 gap-1 text-center">
              {WEEKDAYS_SHORT.map((d) => (
                <div key={d} role="columnheader" className="py-1 text-xs font-medium text-muted">
                  {d}
                </div>
              ))}
              {monthGrid(anchor).flat().map(({ day, inMonth }) => {
                const items = groups[day] ?? []
                const extra = factsByDay[day]?.length ?? 0
                return (
                  <button
                    key={day}
                    type="button"
                    role="gridcell"
                    aria-selected={selected === day}
                    aria-label={`${formatDate(day)}: ${
                      items.map((p) => STATUS_META[p.status].label).join(', ') || 'нет тренировок'
                    }`}
                    onClick={() => setSelected(day)}
                    className={cn(
                      'flex min-h-12 min-w-0 flex-col items-center justify-start gap-1 rounded-md py-1 text-sm tabular-nums',
                      inMonth ? 'text-fg' : 'text-muted/60',
                      selected === day ? 'bg-accent-soft' : 'hover:bg-surface-2',
                    )}
                  >
                    {/* Сегодня — число в акцентном кружке. */}
                    <span
                      className={cn(
                        'flex size-7 items-center justify-center rounded-full',
                        day === todayIso() && 'bg-accent font-semibold text-accent-fg',
                      )}
                    >
                      {Number(day.slice(8))}
                    </span>
                    <span className="flex flex-wrap justify-center gap-0.5">
                      {items.map((p) => (
                        <span key={p.id} className={cn('h-1.5 w-1.5 rounded-full', STATUS_DOT[p.status])} />
                      ))}
                      {extra ? <span className="h-1.5 w-1.5 rounded-full border border-muted" /> : null}
                    </span>
                  </button>
                )
              })}
            </div>
            {selected ? <div className="mt-4">{dayBlock(selected)}</div> : null}
          </>
        )}
      </div>

      <MoveModal item={moving} onClose={() => setMoving(null)} />
      <AddPlannedModal open={addDay !== null} day={addDay ?? todayIso()} onClose={() => setAddDay(null)} />
    </div>
  )
}
